/**
 * WordPress dependencies
 */
import { ToolsPanel } from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';
import { isURL } from '@safe-wordpress/url';

/**
 * External dependencies
 */
import { isDefined } from '@nab/utils';
import type { CssProp, CssPropName } from '@nab/types';

/**
 * Internal dependencies
 */
import { ColorPanelDropdown, TabProps } from './internal/color-panel-dropdown';
import type { ToolsPanelProps } from './types';

import {
	getInheritedValue,
	getPreviewValue,
	getUserValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';

export const ColorPanel = ( {
	value: props,
	onChange,
}: ToolsPanelProps ): JSX.Element => {
	const color = getPreviewValue( props.color );
	const [ backgroundType, backgroundValue ] = getBackgroundPreview( props );

	const isBackgroundColorLocked =
		'color' === backgroundType &&
		'readonly' === props.backgroundColor?.type;

	const isBackgroundGradientLocked =
		'gradient' === backgroundType &&
		'readonly' === props.backgroundImage?.type;
	const isBackgroundImageLocked =
		'image' === backgroundType &&
		'readonly' === props.backgroundImage?.type;
	const isBackgroundImageFullyLocked =
		isBackgroundImageLocked &&
		'readonly' === props.backgroundAttachment?.type &&
		'readonly' === props.backgroundPosition?.type &&
		'readonly' === props.backgroundRepeat?.type &&
		'readonly' === props.backgroundSize?.type;

	const backgroundColorTab: TabProps = {
		type: 'color',
		key: 'background-color',
		label: _x( 'Color', 'text', 'nelio-ab-testing' ),
		setValue: ( background ) => onChange( { background } ),
		userValue:
			'color' === backgroundType
				? getUserValue( props.backgroundColor )
				: undefined,
		inheritedValue:
			'color' === backgroundType
				? getInheritedValue( props.backgroundColor )
				: undefined,
	};

	const backgroundGradientTab: TabProps = {
		type: 'gradient',
		key: 'background-gradient',
		label: _x( 'Gradient', 'text', 'nelio-ab-testing' ),
		setValue: ( background ) => onChange( { background } ),
		userValue:
			'gradient' === backgroundType
				? getUserValue( props.backgroundImage )
				: undefined,
		inheritedValue:
			'gradient' === backgroundType
				? getInheritedValue( props.backgroundImage )
				: undefined,
	};

	const backgroundImageTab: TabProps = {
		type: 'image',
		key: 'background-image',
		label: _x( 'Image', 'text', 'nelio-ab-testing' ),
		setValue: ( value ) => {
			const backgroundImage =
				'readonly' === props.backgroundImage?.type || ! value?.url
					? ''
					: `url(${ value?.url })`;
			const backgroundAttachment =
				'readonly' === props.backgroundAttachment?.type
					? ''
					: value?.attachment ?? 'scroll';
			const backgroundPositionX =
				'readonly' === props.backgroundPositionX?.type
					? ''
					: value?.position.split( ' ' )[ 0 ] ?? '';
			const backgroundPositionY =
				'readonly' === props.backgroundPositionY?.type
					? ''
					: value?.position.split( ' ' )[ 1 ] ?? '';
			const backgroundRepeat =
				'readonly' === props.backgroundRepeat?.type
					? ''
					: value?.repeat ?? '';
			const backgroundSize =
				'readonly' === props.backgroundSize?.type
					? ''
					: value?.size ?? 'cover';

			const position =
				backgroundPositionX && backgroundPositionY
					? {
							backgroundPosition: `${ backgroundPositionX } ${ backgroundPositionY }`,
					  }
					: { backgroundPositionX, backgroundPositionY };

			const backgroundProps = {
				backgroundAttachment,
				...position,
				backgroundRepeat,
				backgroundSize,
			};

			onChange(
				backgroundImage
					? {
							background: backgroundImage,
							...backgroundProps,
					  }
					: backgroundProps
			);
		},
		userValue:
			'image' === backgroundType
				? {
						url: backgroundValue,
						attachment: getPreviewValue(
							props.backgroundAttachment
						),
						position: getBackgroundPosition( props ),
						repeat: getPreviewValue( props.backgroundRepeat ),
						size: getPreviewValue( props.backgroundSize ),
				  }
				: undefined,
		inheritedValue:
			'image' === backgroundType &&
			hasInheritedValue( props.backgroundImage )
				? {
						url: getInheritedValue( props.backgroundImage ) ?? '',
						attachment:
							getInheritedValue( props.backgroundImage ) ?? '',
						position: getBackgroundPosition( props ) ?? '',
						repeat:
							getInheritedValue( props.backgroundRepeat ) ?? '',
						size: getInheritedValue( props.backgroundSize ) ?? '',
				  }
				: undefined,
		disabledProps: [
			'readonly' === props.backgroundImage?.type
				? ( 'backgroundImage' as const )
				: undefined,
			'readonly' === props.backgroundAttachment?.type
				? ( 'backgroundAttachment' as const )
				: undefined,
			'readonly' === props.backgroundPositionX?.type
				? ( 'backgroundPositionX' as const )
				: undefined,
			'readonly' === props.backgroundPositionY?.type
				? ( 'backgroundPositionY' as const )
				: undefined,
			'readonly' === props.backgroundRepeat?.type
				? ( 'backgroundRepeat' as const )
				: undefined,
			'readonly' === props.backgroundSize?.type
				? ( 'backgroundSize' as const )
				: undefined,
		].filter( isDefined ),
	};

	const backgroundTabs = ( (): Readonly< [ TabProps, ...TabProps[] ] > => {
		if ( isBackgroundColorLocked ) {
			return [ backgroundColorTab ];
		}

		if ( isBackgroundGradientLocked ) {
			return [ backgroundGradientTab ];
		}

		if ( isBackgroundImageLocked ) {
			return [ backgroundImageTab ];
		}

		return [
			backgroundColorTab,
			backgroundGradientTab,
			backgroundImageTab,
		];
	} )();

	return (
		<ToolsPanel
			className="nab-tools-panel"
			label={ _x( 'Color', 'text', 'nelio-ab-testing' ) }
			resetAll={ () =>
				onChange( {
					color: undefined,
					background: undefined,
				} )
			}
		>
			<div className="color-block-support-panel__inner-wrapper">
				<ColorPanelDropdown
					disabled={
						isBackgroundColorLocked ||
						isBackgroundGradientLocked ||
						isBackgroundImageFullyLocked
					}
					label={ _x( 'Background', 'text', 'nelio-ab-testing' ) }
					hasValue={ () =>
						hasUserValue( props.backgroundColor ) ||
						hasUserValue( props.backgroundImage )
					}
					onSelect={ () =>
						onChange( {
							background:
								hasInheritedValue( props.backgroundColor ) ||
								hasInheritedValue( props.backgroundImage )
									? ''
									: undefined,
						} )
					}
					onDeselect={ () => onChange( { background: undefined } ) }
					isShownByDefault={
						hasInheritedValue( props.backgroundColor ) ||
						hasInheritedValue( props.backgroundImage )
					}
					indicators={ [
						isURL( backgroundValue )
							? `url(${ backgroundValue })`
							: backgroundValue,
					] }
					tabs={ backgroundTabs }
				/>
				<ColorPanelDropdown
					disabled={ 'readonly' === props.color?.type }
					label={ _x( 'Text', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.color ) }
					onSelect={ () =>
						onChange( {
							color: hasInheritedValue( props.color )
								? ''
								: undefined,
						} )
					}
					onDeselect={ () => onChange( { color: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.color ) }
					indicators={ [ color ] }
					tabs={ [
						{
							type: 'color',
							key: 'text-color',
							label: _x( 'Text', 'text', 'nelio-ab-testing' ),
							setValue: ( v ) => onChange( { color: v } ),
							userValue: getUserValue( props.color ) || undefined,
							inheritedValue: getInheritedValue( props.color ),
						},
					] }
				/>
			</div>
		</ToolsPanel>
	);
};

// =======
// HELPERS
// =======

function getBackgroundPreview(
	props: Partial< Record< CssPropName, CssProp > >
): [ 'color' | 'gradient' | 'image', string ] {
	const backgroundImage = [ 'none', 'initial', 'inherit' ].includes(
		getPreviewValue( props.backgroundImage )
	)
		? ''
		: getPreviewValue( props.backgroundImage );
	const gradient = backgroundImage.includes( 'url(' ) ? '' : backgroundImage;
	const image = gradient ? '' : backgroundImage;

	if ( image ) {
		return [
			'image',
			image
				.trim()
				.replace( /^url\("/, '' )
				.replace( /"\)$/, '' ),
		];
	}

	if ( gradient ) {
		return [ 'gradient', gradient ];
	}

	const color = getPreviewValue( props.backgroundColor );
	return [ 'color', color ];
}

function getBackgroundPosition(
	props: Partial< Record< CssPropName, CssProp > >
): string {
	const x = getPreviewValue( props.backgroundPositionX ) || '0';
	const y = getPreviewValue( props.backgroundPositionY ) || '0';
	return `${ x } ${ y }`.trim();
}
